import React, { useEffect, useLayoutEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { SearchPrivacy } from "./components/SearchPrivacy";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { ContentProvider, useContentApi } from "@/context/ContentContext";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import MilestoneBanner from "@/components/MilestoneBanner";
import MaintenancePage from "@/components/MaintenancePage";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import ExpressCheckout from "@/pages/ExpressCheckout";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import Donate from "@/pages/Donate";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import Admin from "@/pages/Admin";
import Account from "@/pages/Account";
import AuthCallback from "@/pages/AuthCallback";
import Unsubscribe from "@/pages/Unsubscribe";
import Ledger from "@/pages/Ledger";
import SpendDetail from "@/pages/SpendDetail";
import EventCheckIn from "@/pages/EventCheckIn";
import Impact from "@/pages/Impact";
import FundraisePage from "@/pages/FundraisePage";
import CampaignPage from "@/pages/CampaignPage";
import ManageSubscription from "@/pages/ManageSubscription";
import { Financials, ApplyContractor, ApplyGrant } from "@/pages/PublicExtras";
import Portal from "@/pages/Portal";
import BoardPortal from "@/pages/BoardPortal";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ConfirmEmailChange from "@/pages/ConfirmEmailChange";
import LegalChanges from "@/pages/LegalChanges";
import Privacy from "@/pages/Privacy";
import Polls from "@/pages/Polls";
import Raffles from "@/pages/Raffles";
import RaffleDetail from "@/pages/RaffleDetail";
import RaffleCompliance from "@/pages/RaffleCompliance";
import { SharedPayPalProvider } from "@/context/PayPalContext";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import ServiceSubmit from "@/pages/ServiceSubmit";
import Directory from "@/pages/Directory";
import Learn from "@/pages/Learn";
import LearnCourse from "@/pages/LearnCourse";
import VolunteerPortal from "@/pages/VolunteerPortal";
import ClaimAccount from "@/pages/ClaimAccount";

/**
 * ScrollToTop — resets scroll to top whenever the route changes.
 * Uses three layered strategies because React Router v7 + StrictMode can
 * miss a single-tick reset:
 *   1) useLayoutEffect — fires synchronously before paint
 *   2) useEffect       — fires after commit (catches late mount)
 *   3) requestAnimationFrame — catches any layout-shift after content loads
 * Skips when the URL contains a hash anchor so "#section" jumps still work.
 */
const ScrollToTop = () => {
    const { pathname, hash, key } = useLocation();
    const scrollUp = () => {
        try { window.scrollTo(0, 0); } catch (e) { /* legacy browser */ }
        if (document.documentElement) document.documentElement.scrollTop = 0;
        if (document.body) document.body.scrollTop = 0;
    };
    useLayoutEffect(() => {
        if (hash) return;
        scrollUp();
    }, [pathname, hash, key]);
    useEffect(() => {
        if (hash) return;
        scrollUp();
        const id = window.requestAnimationFrame(scrollUp);
        const t = window.setTimeout(scrollUp, 80);
        return () => { window.cancelAnimationFrame(id); window.clearTimeout(t); };
    }, [pathname, hash, key]);
    return null;
};

const AdminBodyClass = () => {
    const { user } = useAuth();
    const location = useLocation();
    useEffect(() => {
        const isAdminUser = user?.role === "admin";
        // Suppress the "Edit mode" badge while on the /admin dashboard itself
        const onAdminPage = location.pathname.startsWith("/admin");
        if (isAdminUser && !onAdminPage) {
            document.body.classList.add("admin-edit-mode");
        } else {
            document.body.classList.remove("admin-edit-mode");
        }
        return () => document.body.classList.remove("admin-edit-mode");
    }, [user, location.pathname]);
    return null;
};

const AppRoutes = () => {
    const location = useLocation();
    const { user } = useAuth();
    const { c, loaded } = useContentApi();

    // Process Emergent OAuth session_id fragment synchronously before any other routing
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }

    // Site-wide maintenance override — admins, /login, /admin, /coming-soon,
    // and the dedicated /maintenance-preview route always bypass so they can
    // keep working / toggle maintenance off again.
    const maintenanceOn = loaded && c("maintenance.enabled", "false") === "true";
    const onBypassRoute =
        location.pathname.startsWith("/admin") ||
        location.pathname.startsWith("/login") ||
        location.pathname.startsWith("/coming-soon") ||
        location.pathname.startsWith("/maintenance-preview");
    const isAdmin = user?.role === "admin";
    if (maintenanceOn && !isAdmin && !onBypassRoute) {
        return <MaintenancePage variant="maintenance" />;
    }

    // Chromeless magic-link routes — no Navbar / Footer / sidebar so the
    // board view feels like a focused dashboard, not the public site.
    if (location.pathname.startsWith("/board")) {
        return (
            <>
                <ScrollToTop />
                <Routes>
                    <Route path="/board" element={<BoardPortal />} />
                </Routes>
            </>
        );
    }

    return (
        <>
            <ScrollToTop />
            <AdminBodyClass />
            <MilestoneBanner />
            <Navbar />
            <main id="main" className="lg:pl-20">
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/shop" element={<Shop />} />
                    <Route path="/product/:id" element={<ProductDetail />} />
                    <Route path="/buy/:id" element={<ExpressCheckout />} />
                    <Route path="/cart" element={<Cart />} />
                    <Route path="/checkout" element={<Checkout />} />
                    <Route path="/donate" element={<Donate />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/admin/raffles/:raffleId/compliance" element={<RaffleCompliance />} />
                    <Route path="/account" element={<Account />} />
                    <Route path="/coming-soon" element={<MaintenancePage variant="coming_soon" />} />
                    {/* Admin-only preview/edit of the maintenance page itself */}
                    <Route
                        path="/maintenance-preview"
                        element={
                            user?.role === "admin"
                                ? <MaintenancePage variant="maintenance" adminPreview />
                                : <Home />
                        }
                    />
                    <Route path="/unsubscribe" element={<Unsubscribe />} />
                    <Route path="/ledger" element={<Ledger />} />
                    <Route path="/ledger/spend/:id" element={<SpendDetail />} />
                    <Route path="/admin/events/:eventId/checkin" element={<EventCheckIn />} />
                    <Route path="/impact" element={<Impact />} />
                    <Route path="/fundraise/:slug" element={<FundraisePage />} />
                    <Route path="/c/:slug" element={<CampaignPage />} />
                    <Route path="/donate/manage" element={<ManageSubscription />} />
                    <Route path="/financials" element={<Financials />} />
                    <Route path="/apply/contractor" element={<ApplyContractor />} />
                    <Route path="/apply/grant" element={<ApplyGrant />} />
                    <Route path="/portal" element={<Portal />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/confirm-email-change" element={<ConfirmEmailChange />} />
                    <Route path="/legal/changes" element={<LegalChanges />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/polls" element={<Polls />} />
                    <Route path="/raffles" element={<Raffles />} />
                    <Route path="/raffles/:slug" element={<RaffleDetail />} />
                    <Route path="/services" element={<Services />} />
                    <Route path="/services/submit" element={<ServiceSubmit />} />
                    <Route path="/services/:id" element={<ServiceDetail />} />
                    <Route path="/directory" element={<Directory />} />
                    <Route path="/learn" element={<Learn />} />
                    <Route path="/learn/:slug" element={<LearnCourse />} />
                    <Route path="/volunteer-portal" element={<VolunteerPortal />} />
                    <Route path="/claim-account" element={<ClaimAccount />} />
                </Routes>
            </main>
            <div className="lg:pl-20">
                <Footer />
            </div>
        </>
    );
};

function App() {
    return (
        <HelmetProvider>
            <div className="App">
                <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-[#E53935] focus:text-white focus:px-4 focus:py-2 focus:rounded-full">
                    Skip to content
                </a>
                <BrowserRouter>
                    <SearchPrivacy>
                    <AuthProvider>
                        <CartProvider>
                            <ContentProvider>
                                <SharedPayPalProvider>
                                <AppRoutes />
                                <Toaster position="top-center" richColors />
                                </SharedPayPalProvider>
                            </ContentProvider>
                        </CartProvider>
                    </AuthProvider>
                    </SearchPrivacy>
                </BrowserRouter>
            </div>
        </HelmetProvider>
    );
}

export default App;
